import { useState } from 'react';
import { validateInput, sanitizeInput, checkRateLimit, logSecurityEvent } from '@/utils/securityUtils';
import { toast } from 'sonner';

interface ValidationErrors {
  [key: string]: string;
}

interface FormValidationConfig {
  rateLimitAction?: string;
  rateLimitIdentifier?: string;
  rateLimitLimit?: number;
  rateLimitWindow?: number;
}

export function useFormValidation(config?: FormValidationConfig) {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name: string, value: string): string | null => {
    switch (name) {
      case 'email':
        return validateInput.email(value) ? null : 'Please enter a valid email address';
      case 'phone':
        return validateInput.phone(value) ? null : 'Please enter a valid phone number (10-15 digits)';
      case 'name':
      case 'customerName':
        return validateInput.name(value) ? null : 'Name must be between 1-100 characters';
      case 'message':
        return validateInput.message(value) ? null : 'Message must be at least 10 characters long';
      case 'address':
        return validateInput.address(value) ? null : 'Please enter a valid address';
      case 'postcode':
        return validateInput.postcode(value) ? null : 'Please enter a valid UK postcode';
      default:
        return null;
    }
  };

  const validateForm = (formData: Record<string, string>): boolean => {
    const newErrors: ValidationErrors = {};
    
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sanitizeFormData = (formData: Record<string, string>): Record<string, string> => {
    const sanitized: Record<string, string> = {};
    
    Object.entries(formData).forEach(([key, value]) => {
      sanitized[key] = sanitizeInput.string(value);
    });

    return sanitized;
  };

  const submitWithRateLimit = async (
    formData: Record<string, string>,
    submitAction: (data: Record<string, string>) => Promise<void>
  ): Promise<boolean> => {
    setIsSubmitting(true);
    
    try {
      // Validate form
      if (!validateForm(formData)) {
        toast.error('Please correct the errors in the form');
        return false;
      }

      // Check rate limiting if configured
      if (config?.rateLimitAction && config?.rateLimitIdentifier) {
        try {
          const rateLimitResult = await checkRateLimit(
            config.rateLimitIdentifier,
            config.rateLimitAction,
            config.rateLimitLimit || 5,
            config.rateLimitWindow || 60
          );

          if (!rateLimitResult.allowed) {
            await logSecurityEvent({
              event_type: 'rate_limit_exceeded',
              details: `Rate limit exceeded for ${config.rateLimitAction}`,
              severity: 'medium'
            });
            
            toast.error(`Too many requests. Please try again in ${Math.ceil(rateLimitResult.retryAfter)} seconds.`);
            return false;
          }
        } catch (rateLimitError) {
          console.warn('Rate limit check failed:', rateLimitError);
          // Continue with submission if rate limiting fails
        }
      }

      // Sanitize data
      const sanitizedData = sanitizeFormData(formData);

      // Submit the form
      await submitAction(sanitizedData);
      
      // Clear form errors on success
      setErrors({});
      return true;
      
    } catch (error) {
      console.error('Form submission error:', error);
      
      await logSecurityEvent({
        event_type: 'suspicious_activity',
        details: `Form submission failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'low'
      });
      
      toast.error('Submission failed. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearErrors = () => setErrors({});
  
  const getFieldError = (fieldName: string): string | undefined => errors[fieldName];

  return {
    errors,
    isSubmitting,
    validateField,
    validateForm,
    sanitizeFormData,
    submitWithRateLimit,
    clearErrors,
    getFieldError
  };
}